<script setup>
import { computed } from 'vue'
import { Head, Link, usePage } from '@inertiajs/vue3'
import {
    ArrowRight,
    CalendarCheck,
    ClipboardCheck,
    LockKeyhole,
    Mail,
    ShieldCheck,
} from 'lucide-vue-next'
import ApplicationLogo from '@/Components/ApplicationLogo.vue'

const props = defineProps({
    title: String,
    appName: { type: String, default: 'Q Banquetes' },
})

const page = usePage()
const user = computed(() => page.props.auth?.user)
const portalHref = computed(() => user.value ? route('dashboard.index') : '/dashboard')
const adminHref = computed(() => user.value ? route(page.props.adminHomeRoute || 'admin.dashboard') : '/admin')
</script>

<template>
    <Head :title="title || 'Portal Q Banquetes'">
        <meta
            name="description"
            content="Portal de clientes Q Banquetes para revisar cotizaciones, seguimiento de eventos y acceso administrativo."
        />
        <meta name="robots" content="noindex, follow" />
    </Head>

    <div class="min-h-screen bg-[#f7f3eb] text-[#151515]">
        <header class="border-b border-[#d8c49a]/45 bg-[#090909]">
            <div class="mx-auto flex max-w-6xl items-center justify-between gap-4 px-5 py-4 sm:px-6">
                <Link :href="route('home')" class="flex items-center gap-3">
                    <ApplicationLogo class="h-11 w-auto max-w-[154px] object-contain" />
                </Link>

                <div class="flex items-center gap-2">
                    <a
                        href="https://qbanquetes.com/"
                        class="hidden rounded-full border border-white/15 px-4 py-2 text-sm font-semibold text-white/80 transition hover:border-[#d8b15f] hover:text-white sm:inline-flex"
                    >
                        Sitio público
                    </a>
                    <a
                        :href="adminHref"
                        class="inline-flex items-center gap-2 rounded-full bg-[#c79a3d] px-4 py-2 text-sm font-semibold text-black transition hover:bg-[#d8b15f]"
                    >
                        Admin
                    </a>
                </div>
            </div>
        </header>

        <main>
            <section class="mx-auto grid min-h-[calc(100vh-76px)] max-w-6xl items-center gap-10 px-5 py-12 sm:px-6 lg:grid-cols-[1.05fr_0.95fr] lg:py-16">
                <div class="max-w-2xl">
                    <p class="mb-5 inline-flex items-center gap-2 rounded-full border border-[#d8c49a] bg-white/70 px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] text-[#8a6828]">
                        Portal Q Banquetes
                    </p>

                    <h1 class="font-serif text-4xl leading-tight text-[#111] sm:text-5xl lg:text-6xl">
                        Sigue tu cotización y los detalles de tu evento aquí.
                    </h1>

                    <p class="mt-6 max-w-xl text-base leading-8 text-[#565047] sm:text-lg">
                        Si ya solicitaste una propuesta, entra al portal para revisar el avance,
                        confirmar información y mantener la comunicación con nuestro equipo.
                    </p>

                    <div class="mt-8 flex flex-col gap-3 sm:flex-row">
                        <a
                            :href="portalHref"
                            class="inline-flex items-center justify-center gap-2 rounded-full bg-[#111] px-6 py-3 text-sm font-bold uppercase tracking-[0.08em] text-white transition hover:bg-[#2b2b2b]"
                        >
                            Entrar a mi portal
                            <ArrowRight class="h-4 w-4" />
                        </a>

                        <a
                            :href="adminHref"
                            class="inline-flex items-center justify-center gap-2 rounded-full border border-[#b68b3a] bg-white px-6 py-3 text-sm font-bold uppercase tracking-[0.08em] text-[#111] transition hover:bg-[#fff8eb]"
                        >
                            Acceso administrador
                            <LockKeyhole class="h-4 w-4" />
                        </a>
                    </div>

                    <div class="mt-8 flex flex-col gap-2 text-sm text-[#6b6256] sm:flex-row sm:items-center sm:gap-5">
                        <span class="inline-flex items-center gap-2">
                            <ShieldCheck class="h-4 w-4 text-[#9b742d]" />
                            Acceso seguro
                        </span>
                        <span class="inline-flex items-center gap-2">
                            <Mail class="h-4 w-4 text-[#9b742d]" />
                            Info@qbanquetes.com
                        </span>
                    </div>
                </div>

                <div class="rounded-[2rem] border border-[#d8c49a]/70 bg-white p-6 shadow-[0_24px_70px_rgba(44,34,16,0.12)] sm:p-8">
                    <div class="border-b border-[#eee4d1] pb-6">
                        <p class="text-sm font-bold uppercase tracking-[0.2em] text-[#9b742d]">Clientes</p>
                        <h2 class="mt-3 text-2xl font-serif text-[#111]">Qué puedes consultar</h2>
                    </div>

                    <div class="mt-6 space-y-5">
                        <div class="flex gap-4">
                            <div class="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#fbf3e2] text-[#9b742d]">
                                <ClipboardCheck class="h-5 w-5" />
                            </div>
                            <div>
                                <h3 class="font-semibold text-[#171717]">Cotizaciones</h3>
                                <p class="mt-1 text-sm leading-6 text-[#665d52]">
                                    Revisa propuestas, información enviada y siguientes pasos.
                                </p>
                            </div>
                        </div>

                        <div class="flex gap-4">
                            <div class="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#fbf3e2] text-[#9b742d]">
                                <CalendarCheck class="h-5 w-5" />
                            </div>
                            <div>
                                <h3 class="font-semibold text-[#171717]">Seguimiento de evento</h3>
                                <p class="mt-1 text-sm leading-6 text-[#665d52]">
                                    Mantén fechas, invitados, lugar y detalles importantes organizados.
                                </p>
                            </div>
                        </div>

                        <div class="flex gap-4">
                            <div class="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#fbf3e2] text-[#9b742d]">
                                <LockKeyhole class="h-5 w-5" />
                            </div>
                            <div>
                                <h3 class="font-semibold text-[#171717]">Panel administrativo</h3>
                                <p class="mt-1 text-sm leading-6 text-[#665d52]">
                                    El mismo login permite al equipo autorizado entrar al panel admin.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div class="mt-8 rounded-2xl bg-[#111] p-5 text-white">
                        <p class="text-sm text-white/70">¿Aún no tienes cotización?</p>
                        <a
                            href="https://qbanquetes.com/#cotizacion"
                            class="mt-2 inline-flex items-center gap-2 font-semibold text-[#d8b15f] transition hover:text-[#f1d79c]"
                        >
                            Solicítala desde qbanquetes.com
                            <ArrowRight class="h-4 w-4" />
                        </a>
                    </div>
                </div>
            </section>
        </main>
    </div>
</template>
